<template>
    <div id="windy" :style="windyStyle"></div>
</template>

<script>
import { eventBus } from '@shared/stores/globalState'

export default {
    name: 'WindyMap',
    props: {
        dataField: {
            type: Array,
            default: () => []
        },
        mapType: {
            type: String,
            default: 'wind'
        }
    },
    data() {
        return {
            windyInstance: null,
            windyMap: null,
            windyMarkers: [],
            windyMarkerGroup: null,
            currentOverlay: 'wind'
        }
    },
    computed: {
        visibleFields() {
            const filtered = this.dataField.filter(field => {
                if (!field) return false;

                const lat = parseFloat(field.lat || field.latitude);
                const lng = parseFloat(field.lng || field.longitude);

                if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                    console.debug('❌ 無效坐標的字段:', field.name, {
                        原始lat: field.lat || field.latitude,
                        原始lng: field.lng || field.longitude,
                        轉換後lat: lat,
                        轉換後lng: lng
                    });
                    return false;
                }

                return true;
            }).map(field => {
                const processedField = {
                    ...field,
                    lat: parseFloat(field.lat || field.latitude),
                    lng: parseFloat(field.lng || field.longitude)
                };

                return processedField;
            });
            return filtered;
        },

        windyStyle() {
            const isWindyActive = ['wind', 'temp', 'pressure'].includes(this.mapType);

            const baseStyle = {
                height: this.fieldScope.isMobile ? 'calc(100% + 10px)' : 'calc(100% + 15px)',
                width: this.fieldScope.isMobile ? '100%' : 'calc(100%)',
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: this.fieldScope.isMobile ? 0 : '0',
                right: '0'
            };

            if (isWindyActive) {
                return {
                    ...baseStyle,
                    zIndex: 15,
                    visibility: 'visible',
                    opacity: 1,
                    pointerEvents: 'auto'
                };
            } else {
                return {
                    ...baseStyle,
                    zIndex: -1,
                    visibility: 'hidden',
                    opacity: 0,
                    pointerEvents: 'none'
                };
            }
        }
    },
    watch: {
        mapType: {
            handler(newType) {
                if (['wind', 'temp', 'pressure'].includes(newType)) {
                    this.changeOverlay(newType);
                }
            },
            immediate: true
        },
        visibleFields: {
            handler() {
                this.$nextTick(() => {
                    this.updateWindyMarkers();
                });
            },
            deep: true
        }
    },
    async mounted() {
        console.debug('🚀 開始 Windy 地圖初始化');

        await this.initWindy();

        eventBus.on('ui:map-type-change', this.handleMapTypeChange);

        console.debug('✅ Windy 地圖初始化完成');
    },
    beforeUnmount() {
        console.debug('🧹 正在清理 WindyMap 組件...');

        eventBus.off('ui:map-type-change', this.handleMapTypeChange);

        this.clearWindyMarkers();
        if (this.windyMarkerGroup) {
            this.windyMarkerGroup = null;
        }

        if (this.windyInstance) {
            try {
                this.windyInstance.destroy();
            } catch (error) {
                console.warn('清除 Windy 實例時出錯:', error);
            }
            this.windyInstance = null;
        }

        console.debug('✅ WindyMap 組件清理完成');
    },
    methods: {
        firstLetter(name) {
            const nameArray = name.split('-');
            if (nameArray.length > 1) {
                return nameArray[1].charAt(0).toUpperCase();
            }
            return name.charAt(0).toUpperCase();
        },

        async initWindy() {
            return new Promise((resolve, reject) => {
                try {
                    if (this.windyInstance) {
                        console.debug('Windy 已經初始化，跳過重複初始化');
                        resolve();
                        return;
                    }

                    if (!window.windyInit) {
                        console.error('Windy API 未載入');
                        reject(new Error('Windy API 未載入'));
                        return;
                    }

                    console.debug('開始初始化 Windy...');
                    const options = {
                        key: 'vVPWSE8VfO4ZzuDqVCImIgXM3n3VoljT',
                        verbose: true,
                        element: document.getElementById('windy')
                    };

                    window.windyInit(options, windyAPI => {
                        console.debug('Windy callback 被調用');
                        this.windyInstance = windyAPI;
                        this.windyMap = windyAPI.map;

                        windyAPI.store.set('overlay', this.mapType || 'wind');
                        windyAPI.store.set('level', 'surface');

                        this.initWindyMarkers();

                        console.debug('Windy 完全初始化完成');
                        resolve();
                    });
                } catch (error) {
                    console.error('Windy 初始化失敗:', error);
                    reject(error);
                }
            });
        },

        initWindyMarkers() {
            if (!this.windyInstance || !this.windyMap) {
                console.warn('⚠️ Windy 實例未準備好，無法初始化標記');
                return;
            }

            console.debug('🗺️ 初始化 Windy 標記群組');

            if (window.L) {
                this.windyMarkerGroup = window.L.layerGroup().addTo(this.windyMap);
                console.debug('✅ Windy 標記群組已創建');

                this.updateWindyMarkers();
            } else {
                console.error('❌ Leaflet 未載入，無法創建 Windy 標記');
            }
        },

        updateWindyMarkers() {
            if (!this.windyInstance || !this.windyMap || !this.windyMarkerGroup) {
                return;
            }

            console.debug('🔄 更新 Windy 標記');

            this.clearWindyMarkers();

            this.visibleFields.forEach(field => {
                this.createWindyMarker(field);
            });

            console.debug(`✅ Windy 標記已更新，共 ${this.visibleFields.length} 個標記`);
        },

        createWindyMarker(field) {
            if (!window.L || !this.windyMarkerGroup) {
                return;
            }

            const lat = parseFloat(field.lat);
            const lng = parseFloat(field.lng);

            if (isNaN(lat) || isNaN(lng)) {
                console.error('❌ 無效的 Windy 標記坐標:', field);
                return;
            }

            const markerHtml = `
                <div class="windy-simple-marker" data-field-id="${field.id}" title="${field.name}">
                    ${this.firstLetter(field.name)}
                </div>
            `;

            const customIcon = window.L.divIcon({
                html: markerHtml,
                iconSize: [36, 36],
                iconAnchor: [18, 18],
                popupAnchor: [0, -18],
                className: 'windy-marker-container'
            });

            const marker = window.L.marker([lat, lng], { icon: customIcon });

            marker.on('click', () => {
                console.debug('Windy 標記被點擊:', field.name);
                const fieldTitle = field.name || `案場 ${field.id}`;
                eventBus.emit('view:open', {
                    path: '/field/FieldDetail',
                    title: fieldTitle,
                    options: {
                        fieldId: field.id,
                        orgId: this.fieldScope?.orgId || this.fieldScope?.selectedOrgId,
                        height: 420
                    }
                });
            });

            const popupContent = `
                <div class="windy-popup-content">
                    <strong>${field.name}</strong><br>
                    座標: ${lat.toFixed(6)}, ${lng.toFixed(6)}
                </div>
            `;
            marker.bindPopup(popupContent);

            this.windyMarkerGroup.addLayer(marker);
            this.windyMarkers.push(marker);

            return marker;
        },

        clearWindyMarkers() {
            if (this.windyMarkerGroup) {
                this.windyMarkerGroup.clearLayers();
            }
            this.windyMarkers = [];
            console.debug('🧹 已清除所有 Windy 標記');
        },

        changeOverlay(type) {
            if (!this.windyInstance) {
                return;
            }

            const overlayMap = {
                wind: 'wind',
                temp: 'temp',
                pressure: 'pressure'
            };

            this.currentOverlay = overlayMap[type] || 'wind';
            this.windyInstance.store.set('overlay', this.currentOverlay);
            console.debug(`Windy 覆蓋層設置為: ${this.currentOverlay}`);
        },

        handleMapTypeChange(type) {
            if (['wind', 'temp', 'pressure'].includes(type)) {
                this.changeOverlay(type);
            }
        }
    }
}
</script>

<style lang="stylus" scoped>
#windy
    transition: opacity 0.4s ease, z-index 0s linear 0.2s, visibility 0.4s ease

#windy[style*="opacity: 0"]
    transition: opacity 0.4s ease, z-index 0s linear 0s, visibility 0.4s ease

.windy-simple-marker
    width: 36px
    height: 36px
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)
    border: 3px solid #ffffff
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    font-size: 14px
    font-weight: bold
    color: white
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4), 0 2px 6px rgba(0, 0, 0, 0.2)
    cursor: pointer
    transition: all 0.2s ease

    &:hover
        transform: scale(1.15)
        box-shadow: 0 6px 16px rgba(25, 118, 210, 0.6), 0 3px 8px rgba(0, 0, 0, 0.3)

.windy-marker-container
    background: transparent
    border: none

.windy-popup-content
    font-size: 12px
    line-height: 1.5

    strong
        display: block
        margin-bottom: 4px
        color: #333
</style>

